/**
 * Helper functions for arrays
 * @module
 */

export function containsNulls(list: any[]): boolean {
  return list.some((item) => typeof item === "undefined" || item === null);
}

export function containsFalsy(list: any[]): boolean {
  return list.some((item) => (typeof item === "number" ? false : !item));
}

/**
 * Returns true when both arrays contain the same elements in the same order under strict
 * equality (`===`). Handles `undefined` on either side and short-circuits on identity.
 */
export function arrayShallowEquals<T>(a: readonly T[] | undefined, b: readonly T[] | undefined): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

// recursive function to flatten an array of arrays of infinite depth
export function arrayFlatten<T>(arr: any[]): T[] {
  return arr.reduce(
    (flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? arrayFlatten(toFlatten) : toFlatten),
    [],
  );
}

/**
 * Converts an array of strings to a sentence.
 * @param arr - An array of strings.
 */

export function stringArrayToSentence(arr: string[]): string {
  if (arr.length < 2) {
    return arr[0] ?? "";
  }
  return `${arr.slice(0, -1).join(", ")} and ${arr.slice(-1)[0]}`;
}

export function cartesianProduct<T>(arrays: T[][]): T[][] {
  function helper(arr: T[], i: number): T[][] {
    if (i === arrays.length) {
      return [arr];
    }
    const result: T[][] = [];
    for (let j = 0; j < arrays[i].length; j++) {
      result.push(...helper([...arr, arrays[i][j]], i + 1));
    }
    return result;
  }
  return helper([], 0);
}

export function cartesianProduct2<A, B>(arrayA: A[], arrayB: B[]): [A, B][] {
  const cartesianProduct: [A, B][] = [];
  for (const a of arrayA) {
    for (const b of arrayB) {
      cartesianProduct.push([a, b]);
    }
  }
  return cartesianProduct;
}

export function cartesianProduct3<A, B, C>(arrayA: A[], arrayB: B[], arrayC: C[]): [A, B, C][] {
  const cartesianProduct: [A, B, C][] = [];
  for (const a of arrayA) {
    for (const b of arrayB) {
      for (const c of arrayC) {
        cartesianProduct.push([a, b, c]);
      }
    }
  }
  return cartesianProduct;
}

export function groupBy<T, K extends string | number>(arr: T[], key: (item: T) => K): Record<K, T[]> {
  return arr.reduce(
    (acc, item) => {
      const k = key(item);
      if (!acc[k]) {
        acc[k] = [];
      }
      acc[k].push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}

export function arrayMove<T>(arr: T[], from: number, to: number) {
  const element = arr[from];
  arr.splice(from, 1);
  arr.splice(to, 0, element);
}

export function arrayUnique<T>(...arr: T[][]): T[] {
  return [...new Set(arr.flat())];
}

// Using key:string (vs generic index signature) allows T to pass through dynamically, eg. table[id] = T, instead of any
export function arrayToTable<T extends Record<string, unknown>>(arr: T[], key: string): Record<string, T> {
  const table = {} as Record<string, T>;
  for (const item of arr) {
    table[item[key] as string] = item;
  }
  return table;
}

// Partition an array into multiple arrays based on given predicates
export function arrayPartition<T>(array: T[], ...predicates: ((element: T) => boolean)[]): T[][] {
  if (predicates.length < 1) {
    return [array];
  }
  return array.reduce(
    (result: T[][], element: T) => {
      let passes: boolean = false;
      for (const [index, predicate] of predicates.entries()) {
        if (predicate(element)) {
          passes = true;
          result[index].push(element);
        }
      }
      if (!passes) {
        result[predicates.length].push(element);
      }
      return result;
    },
    Array.from({ length: predicates.length + 1 }, () => []),
  );
}

/**
 * Find the intersection of two arrays
 * @returns an array containing a unique instance of each member of the input arrays which appears in both arrays
 */
export function arrayIntersection<T>(arr1: T[], arr2: T[]): T[] {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);
  const result: T[] = [];
  for (const item of set2) {
    if (set1.has(item)) {
      result.push(item);
    }
  }
  return result;
}

export function arrayIsSortedAscending<T>(arr: readonly T[]): boolean {
  return arr.every((value, index, array) => {
    // The first element has no predecessor, so we return true for index 0
    if (index === 0) {
      return true;
    }
    // Check if the current value is greater than or equal to the previous value
    return value >= array[index - 1];
  });
}
